// src/components/TeamMemberCard.js
import React from 'react';
import { Paper, Avatar, Typography, Box } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

export interface TeamMember {
    name: string;
    id: string;
    role: string;
    language: string;
    description: string;
    image: string; // Assuming image is always a string path/URL
};

interface TeamMemberCardProps {
    member: TeamMember | null | undefined; // Accept TeamMember or null/undefined for safety
};
    
const TeamMemberCard: React.FC<TeamMemberCardProps> = ({ member }) => {
    // Type guard: Check if member exists before accessing properties
    if (!member) {
      return null; // Or return some placeholder/error component
    }
  
  return (
    <Paper
      elevation={3} // Add slight elevation
      sx={{
        p: 3,
        width: '100%', // Take full width of its container (slide padding container)
        height: '100%', // Ensure paper takes full slide height for alignment
        minHeight: { xs: 380, sm: 400 }, // Minimum height for consistency
        display: 'flex', // Use flex for content alignment
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)', // Enhanced hover
          boxShadow: '0 12px 24px rgba(0,0,0,0.15)', // Enhanced shadow
        },
      }}
    >
      <Avatar
        src={member.image}
        alt={member.name}
        sx={{
          width: { xs: 100, md: 120 },
          height: { xs: 100, md: 120 },
          mb: 2,
          border: '3px solid',
          borderColor: 'primary.main',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        }}
      />
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontWeight: 600, mt: 1 }} // Added top margin
      >
        {member.name}
      </Typography>
      <Typography
        variant="body2"
        color="primary" // Using primary color for role
        gutterBottom
        sx={{
          mb: 1,
          fontWeight: 500,
        }}
      >
        {member.role}
      </Typography>
      <Typography
        variant="body2"
        color="primary" // Using primary color for language
        gutterBottom
        sx={{
          mb: 1,
          fontWeight: 500,
        }}
      >
        Fluent in: {member.language}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mt: 1,
          lineHeight: 1.6,
          flexGrow: 1, // Allow description to take up remaining space
           // Optional: Text truncation for very long descriptions
           overflow: 'hidden',
           textOverflow: 'ellipsis',
           display: '-webkit-box',
           WebkitLineClamp: '4', // Limit to 4 lines
           WebkitBoxOrient: 'vertical',
        }}
      >
        {member.description}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mt: 2,
          cursor: 'pointer',
          color: 'primary.main',
          fontWeight: 500,
          fontSize: 15,
          transition: 'color 0.2s',
          width: '100%',
          '&:hover': { color: 'primary.dark' }
        }}
      >
        Read More
        <ArrowForwardIosIcon sx={{ fontSize: 15, ml: 0.5 }} />
      </Box>
    </Paper>
  );
};

export default TeamMemberCard;